import type { AxiosError, AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";
import { axiosInstance, axiosPrivate, axiosPrivateBlob } from "./axios";
import { queryClient } from "@core/libs/react-query/react-queryClient";
import { refreshToken } from "./services/authRequests";
import { toast } from "@core/components";
import i18n from "@core/libs/i18n/i18n";
import { logger } from "@core/utils/logger";

// ── Timestamp key for request duration tracking
const TIMING_KEY = "__requestStartTime";

type TimedRequest = InternalAxiosRequestConfig & {
  [TIMING_KEY]?: number;
};

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// ── HTTP logger interceptors (dev + staging only, no-op in production)
const attachHttpLogger = (instance: AxiosInstance): void => {
  const log = logger.http;

  instance.interceptors.request.use((config: TimedRequest) => {
    config[TIMING_KEY] = Date.now();
    log.request(
      config.method?.toUpperCase() ?? "REQUEST",
      config.url ?? "",
      config.params as unknown,
    );
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      const config = response.config as TimedRequest;
      const durationMs =
        config[TIMING_KEY] != null ? Date.now() - config[TIMING_KEY] : 0;
      log.response(
        config.method?.toUpperCase() ?? "REQUEST",
        config.url ?? "",
        response.status,
        durationMs,
      );
      return response;
    },
    (error: AxiosError) => {
      const config = error.config as TimedRequest | undefined;
      log.error(
        config?.method?.toUpperCase() ?? "REQUEST",
        config?.url ?? "",
        error.response?.status ?? null,
        error.response?.data,
      );
      return Promise.reject(error);
    },
  );
};

// Attach to all three instances at module load.
// Guaranteed to run because apiInstances.ts has: import "./privateAxios"
attachHttpLogger(axiosInstance);
attachHttpLogger(axiosPrivate);
attachHttpLogger(axiosPrivateBlob);

let isInterceptorSetup = false;

export const setupPrivateInterceptor = (onLogout: () => void): void => {
  if (isInterceptorSetup) return;

  axiosPrivate.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as CustomAxiosRequestConfig;

      if (error.response?.status === 403) {
        const errorCode = (error.response?.data as Record<string, unknown>)?.error;
        if (errorCode === "read_only_mode") {
          toast({
            title: i18n.t("read-only-blocked", { ns: "common" }),
            variant: "warning",
          });
        }
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await refreshToken();
          queryClient.invalidateQueries({ queryKey: ["user"] });
          return axiosPrivate(originalRequest as AxiosRequestConfig);
        } catch (err) {
          onLogout();
          queryClient.removeQueries({ queryKey: ["user"] });
          return Promise.reject(err);
        }
      }

      return Promise.reject(error);
    }
  );

  isInterceptorSetup = true;
};
